import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './SmsConsentPage.css';

// showBackButton: only passed true when embedded as a "My Profile" sidenav
// section — the standalone /sms-consent route (linked from the public
// footer, and the one carriers are given for A2P 10DLC campaign review)
// keeps its plain marketing header.
const SmsConsentPage = ({ showBackButton = false }) => {
  const navigate = useNavigate();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <div className="sms-consent-page">
      <section className="page-header">
        <div className="container">
          {showBackButton && (
            <button className="sms-consent-back-btn" onClick={() => navigate(-1)}>
              <span>←</span>
              <span>Back</span>
            </button>
          )}
          <h1>SMS Consent &amp; Messaging Terms</h1>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="sms-consent-content">
            <h2>1. Who Is Texting You</h2>
            <p>
              Rialty.ai ("Rialty," "we," "our," or "us") is a real estate transaction platform for brokerages, agents, buyers, sellers, lenders, and title companies. This page describes our SMS text messaging program, including what you'll receive, how to opt in, and how to opt out.
            </p>

            <h2>2. Message Types</h2>
            <p>Rialty sends transactional text messages only — never marketing or promotional messages. The texts you may receive are:</p>
            <ul>
              <li><b>Transaction invitations:</b> A link to join the transaction workspace for a property, sent to parties participating in an active transaction.</li>
              <li><b>Verification codes:</b> One-time passcodes with secure links used to verify your identity before viewing or signing disclosures.</li>
            </ul>
            <p>Text messages are sent only to parties in an active transaction who have consented to receive them.</p>

            <h2>3. How You Opt In</h2>
            <p>
              You opt in by checking the SMS consent checkbox when you create a Rialty account — a separate, unchecked checkbox from Terms of Service acceptance. Providing your phone number alone does not opt you in; the checkbox must be affirmatively checked.
            </p>
            <p>
              If you're invited to a transaction by someone else (for example, an agent adding you as a buyer, seller, lender, or title company contact) and don't yet have a Rialty account, we confirm your consent directly with you by text before sending anything beyond that confirmation request.
            </p>

            <h2>4. Message Frequency</h2>
            <p>
              Message frequency varies and is driven by your transaction activity — there is no fixed schedule. A transaction with more activity (invitations, disclosures requiring verification) will generate more messages than one that is quiet.
            </p>

            <h2>5. Message and Data Rates</h2>
            <p>
              Message and data rates may apply, depending on your mobile carrier and plan. Rialty does not charge for these messages, but your carrier's standard rates apply.
            </p>

            <h2>6. How to Opt Out</h2>
            <p>
              Reply <b>STOP</b>, <b>UNSUBSCRIBE</b>, <b>CANCEL</b>, <b>END</b>, or <b>QUIT</b> to any message at any time to stop receiving texts from Rialty. You'll receive one final confirmation message, and no further texts will be sent unless you opt back in.
            </p>

            <h2>7. How to Opt Back In</h2>
            <p>
              Reply <b>START</b> to resume receiving transaction texts and verification codes after opting out.
            </p>

            <h2>8. How to Get Help</h2>
            <p>
              Reply <b>HELP</b> to any message, or contact us at <a href="mailto:support@rialty.ai">support@rialty.ai</a>, for assistance with this messaging program.
            </p>

            <h2>9. Sample Messages</h2>
            <p>The exact messages you'll receive when opting in or out:</p>
            <p className="sms-sample">
              <b>Opt-in confirmation:</b><br />
              "Rialty: You are subscribed to transaction alerts and verification codes from Rialty. Msg frequency varies. Msg &amp; data rates may apply. Reply HELP for help, STOP to opt out."
            </p>
            <p className="sms-sample">
              <b>Opt-out confirmation:</b><br />
              "Rialty: You have been unsubscribed and will receive no further messages. Reply START to resubscribe."
            </p>

            <h2>10. Your Information</h2>
            <p>
              No mobile information will be shared with third parties or affiliates for marketing or promotional purposes. Your phone number is used solely to deliver the transactional messages described above. See our{' '}
              <a href="/privacy">Privacy Policy</a> for how we handle your information generally, and our{' '}
              <a href="/terms">Terms of Service</a> for our full terms.
            </p>

            <h2>11. Contact Us</h2>
            <p>
              If you have any questions about this messaging program, please contact us at:
            </p>
            <p className="contact-info">
              <b>Rialty.ai</b><br />
              Email: support@rialty.ai<br />
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SmsConsentPage;
